// shared/api.ts
// server と client で共有する REST API の型定義 (v2)。
// 「ここを 1 箇所変えれば両方の TS が追従する」が AI 開発時の保険。

export type Iryo  = "合計" | "急性期" | "回復期";
export type Yotei = "合計" | "予定"   | "緊急";
export type Granularity = "大" | "中" | "小";
export type Direction   = "higher_is_better" | "lower_is_better" | "neutral";

// ── /api/meta ──────────────────────────────────────────────────────────
export interface DeptRow {
  粒度?:    Granularity;   // v2: 粒度別マスタを返すときに付く
  診療科:   string;
  order:    number;
  color:    string;
  is_total: boolean;
}
export interface MetricRow {
  集計区分:        string;
  metric_group:    string;
  unit:            string;
  direction?:      Direction;
  is_top_kpi?:     boolean;
  has_budget?:     boolean;
  granularities?:  string;
  order:           number;
  description?:    string;     // v2.1: 指標定義の説明 (tooltip 用)
  // [2026-08-22] 検証中の指標。/beta でだけ見せる (GP 新定義の 4 指標)。
  //   server の /api/meta は非 beta ユーザにはこの行自体を返さない。
  //   beta ユーザには返すので、正式版ページ (/) では client 側でも落とす。
  beta_only?:      boolean;
}
// [開発版(新GP) 2026-08-24] GP の定義。"" = 従来 (入院収入 − 出来高換算薬剤材料費)
//   / "jinken" = 新定義 (薬剤材料費差引後入院収入 − 医師人件費)。
//   ★リクエスト単位。server は `X-GP-Mode: jinken` を受けた時だけ "jinken" を返す。
//   UI はこれを見て開発版バナーを出し、URL (/theta) とズレていたら警告する。
export type GpDef = "" | "jinken";

export interface MetaResponse {
  depts:    DeptRow[];      // 後方互換: 粒度=小
  deptsAll: DeptRow[];      // v2: 全粒度
  metrics:  MetricRow[];
  dateMin:  string;
  dateMax:  string;
  builtAt:  string;
  schemaVersion?: string;
  surgeryDepts: string[];   // 手術台帳 大分類 (診療科) 一覧。 入院 (収入分析) とは別物
  surgeryDeptsSmall: string[]; // 手術台帳 小分類 (手術科小分類: 外科→消化器/乳腺内分泌/呼吸器)。 dim=小分類 用
  gpDef?:   GpDef;      // [開発版(新GP)] このリクエストの GP 定義 (X-GP-Mode ヘッダ由来)
}

// ── /api/query/monthly ────────────────────────────────────────────────
export interface QueryFilters {
  granularity?: Granularity;  // default: '小' (後方互換)
  iryo:    Iryo;
  yotei:   Yotei;
  metric:  string;
  dateFrom?: string;
  dateTo?:   string;
}
export interface MonthlyRow {
  date:   string;
  診療科: string;
  // 2026-07-17: number → number | null。 型が実態と違っていた。
  //   server/index.ts:377-380 の FULL OUTER JOIN は act.実績 を素通しするので、
  //   (a) fact_long_all に実績 NULL の行がある (777 の complete() 由来。21,384 行)
  //   (b) 予算はあるが実績が無い月 (未来月)
  //   のとき null が返る。型が number だったため呼び出し側が Number(r.実績) と
  //   無防備に書き、Number(null) === 0 で NULL が 0 セルに化けていた。
  //   (予算 だけ optional で r.予算 != null ガードがあり、実績 と非対称だった)
  実績:    number | null;
  予算?:   number | null;   // v2: 予算 overlay
}
export interface MonthlyResponse {
  rows: MonthlyRow[];
  sqlMs: number;
}

// ── /api/query/ranking ────────────────────────────────────────────────
export interface RankingRow {
  診療科: string;
  total:   number;
  avg?:    number;
  budget?: number;
  prevYearTotal?: number;
}
export interface RankingResponse {
  rows: RankingRow[];
  sqlMs: number;
}

// ── /api/query/summary (KPI 用) ────────────────────────────────────────
export interface SummaryFilters {
  granularity?: Granularity;
  iryo:     Iryo;
  yotei:    Yotei;
  dateFrom?: string;
  dateTo?:   string;
  metrics:  string[];
}
export interface SummaryRow {
  集計区分: string;
  total:     number;
  count:     number;
  avg:       number;
}
export interface SummaryResponse {
  rows: SummaryRow[];
  sqlMs: number;
}

// ── /api/query/kpi (v2 新規) ──────────────────────────────────────────
// 単一指標について「当月 / 前月 / 前年同月 / 当月予算 / 期間累計 / 月平均 / 25M トレンド」
// を 1 リクエストで返す
export interface KpiFilters {
  granularity: Granularity;
  iryo:        Iryo;
  yotei:       Yotei;
  metric:      string;
  asOfMonth:   string;        // YYYY-MM-DD: 「対象月」
  periodFrom:  string;        // YYYY-MM-DD: 期間累計/月平均の起点
  focusedDept?: string;        // v2.1: 特定診療科にフォーカス。null/undefined なら病院全体
}
export interface KpiTrendPoint {
  date: string;
  actual:    number | null;
  budget:    number | null;
  prevYear:  number | null;
}
export interface KpiResponse {
  asOfMonth:   string;
  current:     number | null;
  prevMonth:   number | null;
  prevYear:    number | null;
  budget:      number | null;
  periodTotal: number | null;
  periodAvg:   number | null;
  periodN:     number;
  trend:       KpiTrendPoint[];
  sqlMs:       number;
}

// ── /api/query/heatmap (v2 新規) ──────────────────────────────────────
export interface HeatmapFilters {
  granularity: Granularity;
  iryo:        Iryo;
  yotei:       Yotei;
  metric:      string;
  periodFrom:  string;
  periodTo:    string;
}
export interface HeatmapCell {
  date:    string;
  診療科:  string;
  value:   number;
  budget?: number;
  prevYear?: number;
}
export interface HeatmapResponse {
  rows:    HeatmapCell[];
  sqlMs:   number;
}

// ── /api/query/detail (v2.3 — 元データ明細 fact_long_all subset) ──
// 期間 + 粒度 + 医療区分 + 予定緊急 で絞った全 (診療科 × 集計区分 × 月) 明細
export interface DetailFilters {
  granularity: Granularity;
  iryo?:        Iryo | "全";       // "全" なら 全医療区分
  yotei?:       Yotei | "全";
  periodFrom:   string;
  periodTo:     string;
  metrics?:     string[];          // undefined or 空配列なら 全集計区分
}
export interface DetailRow {
  date:     string;
  粒度:    string;
  診療科:  string;
  医療区分: string;
  予定緊急: string;
  集計区分: string;
  実績:    number;
}
export interface DetailResponse {
  rows:  DetailRow[];
  sqlMs: number;
}

// ── /api/query/kb_revenue (v2.6 — 行為別小計 月次×診療科) ─────────
// 020_kb_all の手術小計/入院料小計/検査小計 等を月次集計したもの
export interface KbRevenueFilters {
  periodFrom: string;
  periodTo:   string;
  metric?:    string;   // 集計区分 (未指定なら全部)
}
export interface KbRevenueRow {
  date:    string;
  診療科:  string;
  集計区分: string;
  実績:    number;
  前年実績?: number;
}
export interface KbRevenueResponse {
  rows:  KbRevenueRow[];
  sqlMs: number;
}

// ── /api/query/surgery (v2.5 — 手術件数 月次集計) ─────────────────
// 03_手術実績.xlsx → fact_surgery_long を集計
export interface SurgeryFilters {
  periodFrom: string;
  periodTo:   string;
  // v2.6b: 集計粒度。"小分類" のとき外科を 消化器/乳腺内分泌/呼吸器 に分割した
  //   fact_surgery_small_long を参照する (202404 以降)。未指定/"大分類" は従来の fact_surgery_long。
  dim?: "大分類" | "小分類";
}
export interface SurgeryMonthlyRow {
  date:      string;
  診療科:    string;
  集計区分:  string;  // 手術件数 / 予定 / 緊急 / 全麻 / 局麻
  実績:      number;
  予算?:     number;
  前年実績?: number;
}
export interface SurgeryResponse {
  rows:    SurgeryMonthlyRow[];
  sqlMs:   number;
}

// ── /api/query/portfolio (v2.2 — 診療科 portfolio bubble chart) ────
// 横軸=予算達成率、縦軸=YoY、サイズ=入院収入 を 診療科別で返す
export interface PortfolioFilters {
  granularity: Granularity;
  iryo:        Iryo;
  yotei:       Yotei;
  asOfMonth:   string;
  periodFrom:  string;
  metric?:     string;   // v2.17: 任意の集計区分。 未指定なら "入院収入"
}
export interface PortfolioRow {
  診療科:  string;
  rev_cur: number | null;   // 当月入院収入
  rev_period: number | null;  // 期間累計
  rev_budget: number | null;  // 当月予算
  rev_prevYear: number | null;
  budget_ratio: number | null; // 達成率
  yoy_ratio: number | null;    // YoY 倍率
}
export interface PortfolioResponse {
  rows: PortfolioRow[];
  sqlMs: number;
}

// ── /api/query/pvm (v2 新規 — 入院収入差異の単価/延数分解) ────────
// 入院収入 = 入院単価 × 入院患者延数 / 1000 (千円)
// ΔRev = Rev_cur - Rev_ref
//   = (Price_cur - Price_ref) * Vol_cur + Price_ref * (Vol_cur - Vol_ref)
//   = price contribution        + volume contribution
// 「ref」は前年同月 (YoY) または 当月予算 (vs budget)。
export interface PvmFilters {
  granularity: Granularity;
  iryo:        Iryo;
  yotei:       Yotei;
  asOfMonth:   string;
  reference:   "前年同月" | "予算";
  focusedDept?: string;  // 指定時は その診療科の rev/price/vol を total に表示 (v2.14)
}
export interface PvmRow {
  診療科:  string;
  rev_cur: number | null;     // 当月実績 (千円)
  rev_ref: number | null;     // 参照値 (千円)
  rev_diff:    number | null; // 差額 (千円)
  price_contrib: number | null;
  volume_contrib: number | null;
  price_cur:  number | null;  // 当月単価 (円/日)
  price_ref:  number | null;
  vol_cur:    number | null;  // 当月延数 (人日)
  vol_ref:    number | null;
}
export interface PvmResponse {
  asOfMonth: string;
  reference: string;
  total:     PvmRow;          // 病院全体 1 行
  byDept:    PvmRow[];        // 診療科別 (粒度=小なら 21科)
  sqlMs:     number;
}

// ── /api/query/route (v2.17 — 入院経路 cross-tab) ─────────────────
// fact_route から (date × 経由 × 予定緊急 × 紹介) の raw 件数を返す。
// クライアントが任意軸 (経由 / 予定緊急 / 紹介 / 経路8 / 科別) で pivot する設計。
//
// scope: granularity + iryo + focusedDept (focusedDept 未指定なら病院全体 anchor)
// 期間: periodFrom 〜 periodTo (asOfMonth と独立、 通常 12-25 ヶ月)
// 科別ランキング (deptBreakdown=true) のときは個別科の月次累計も合わせて返す。
export interface RouteFilters {
  granularity: Granularity;
  iryo:        Iryo;
  periodFrom:  string;        // YYYY-MM-DD
  periodTo:    string;        // YYYY-MM-DD
  focusedDept?: string;        // undefined or "" → 病院全体 anchor
  deptBreakdown?: boolean;    // true → 個別科の集計も含める (RoutePanel 科別 view 用)
}
export interface RouteRow {
  date:     string;
  診療科:   string;
  経由:     string;      // HP / CL / 腎CL / 地域ケア
  予定緊急: string;      // 予定 / 緊急
  紹介:     number;      // 0 / 1
  count:    number;
}
export interface RouteResponse {
  rows:    RouteRow[];      // anchor or focused dept の (date×経由×予定緊急×紹介) 行
  byDept?: RouteRow[];      // deptBreakdown=true の場合のみ。 個別科の同期間累計 (date は periodTo に集約せず月単位で返す)
  asOfMonth: string;        // 末月 (= periodTo)
  sqlMs:   number;
}

// ── /api/query/alerts (v2 新規 — 要確認診療科 4 カテゴリ × TOP6) ────
export interface AlertsFilters {
  granularity: Granularity;
  iryo:        Iryo;
  yotei:       Yotei;
  asOfMonth:   string;
}
export interface AlertItem {
  診療科: string;
  metric: string;
  value:  number;
  comparator: number;     // 比較対象値 (前年/予算/etc)
  diff:   number;
  ratio?: number;
}
export interface AlertsResponse {
  budgetUnder:     AlertItem[];   // 入院収入予算比 未達 TOP6
  yoyDown:         AlertItem[];   // 入院収入前年比 低下 TOP6
  priceDown:       AlertItem[];   // 1日入院単価 低下 TOP6 (入院単価)
  volDown:         AlertItem[];   // 入院患者延数 減少 TOP6
  newPatientDown:  AlertItem[];   // 新規受持患者数 減少 TOP6 (新規入院数)
  stayUp:          AlertItem[];   // 平均在院日数 長期化 TOP6 (前年比 増、高いほど悪い)
  sqlMs: number;
}

// ── /api/query/pl (v2.17 — 経営PL タブ用 損益 月次 + 年計 + 指数) ────
// pl/output/parquet/fact_pl_{monthly,rolling12,indices}.parquet 由来
// monthly:   月次原データ (千円)
// rolling12: 移動 12 ヶ月累計 (千円) — 年計
// indices:   rolling12 を base_month=100 で正規化した指数
// 全レスポンスを 1 ショットで返して client 側で switch する設計 (data 量小、 30KB 以下)
export interface PlMetricRow {
  metric:         string;
  category:       string;     // 収益 / 利益 / 費用_合計 / 費用_材料費内訳
  display_order:  number;
  color:          string;     // hex (例 #0a2463)
  is_overview:    boolean;    // 主要指標年計傾向 (全体) に出すか
  is_material:    boolean;    // 主要指標年計傾向 (材料費内訳) に出すか
}
export interface PlPoint {
  date:   string;             // YYYY-MM-DD
  metric: string;
  value:  number;             // 千円 (monthly/rolling12) or % (indices: 100=基準月)
}
export interface PlIndicesPoint extends PlPoint {
  base_month: string;         // YYYY-MM-DD: 基準月 (= 100)
}
// v2.18: 予算 vs 実績 (当年度) 追加 — fact_pl_budget_monthly + fact_pl_budget_actual_monthly
// budget_monthly:    当年度 12 ヶ月の月次予算 (全 metric)
// budget_actual:     当年度 月次実績 (進行中の月まで)、 (前期比較) 由来の monthly と原則一致
// budget_fiscalYear: 予算の年度 (例 2026 = FY2026 / 4月-3月)
// budget_latestActualMonth: 当年度実績がどこまで埋まっているか (YYYY-MM-DD)
export interface PlBudgetPoint {
  date:    string;            // YYYY-MM-DD
  metric:  string;
  value:   number;            // 千円
}
export interface PlResponse {
  metrics:      PlMetricRow[];
  monthly:      PlPoint[];
  rolling12:    PlPoint[];
  indices:      PlIndicesPoint[];
  baseMonth:    string;       // indices の基準月 (= 計算可能な最古月)
  dateMin:      string;       // monthly の最古月
  dateMax:      string;       // monthly の最新月
  // 予算系 (テーブル無ければ空配列、 fiscalYear=0)
  budgetMonthly:        PlBudgetPoint[];   // 当年度 12 ヶ月分の月次予算
  budgetActual:         PlBudgetPoint[];   // 当年度 月次実績 (進行中の月まで)
  budgetFiscalYear:     number;            // 例 2026 (= FY2026)
  budgetLatestActualMonth: string;         // YYYY-MM-DD (当年度実績の最終月)
  sqlMs:        number;
}

// ── /api/query/disease_ranking, /api/query/doctors (疾患深掘り Phase 1) ──
// fact_disease_adm + dim_dpc6 + dim_doctor の集計。
// 期間 (entdate) + 任意 filter (dep / dpcfy / dpc6 / admdrcd) で 疾患別 KPI ranking を返す。
// 「(未分類)」 と 「収益 NULL」 は デモ誤解防止のため明示除外、 除外件数のみ excludedReasons で返す。
// [7/8桁 2026-09-28] /api/query/dpc_branch_levels — 選択中コード配下で「分岐がある階層」。
//   DPC分析の階層タブのうち、分岐が無い (= 押しても 1 行しか出ない) タブを薄く表示するのに使う。
export interface DpcBranchLevelsResponse {
  prefix: string;          // 問い合わせたコード (6〜13 桁)
  levels: number[];        // prefix より深い階層のうち、配下が 2 つ以上に分かれる階層
}
export interface DiseaseRankingFilters {
  dateFrom:    string;          // "2025-05-01" 等 (entdate 起点)
  dateTo:      string;          // "2026-04-30" 等 (entdate 終点)
  dep?:        string | null;   // dashboard 正規名 (e.g. 循環器内科 / 循内(虚血))。
                                // server で粒度 + bridge により fact_disease_adm.dep 値リストに展開して filter。
  granularity?: Granularity;    // dep の解釈粒度 (default = '小')
  dpcfy?:      number | null;   // DPC 年度 (令和6=2024, 令和8=2026)
  dpc6?:       string | null;   // 疾患コード 6 桁
  admdrcd?:    string | null;   // 入院医師コード
  minCases?:   number;          // 症例数 N 件以上の疾患のみ返す (default 10)
  yotei?:      string | null;   // "予定" / "緊急" / null=全て (fact_disease_adm.予定緊急)
  iryo?:       string | null;   // (廃止) 旧 医療区分。急性期ベース化で server 未使用
  level?:      number | null;   // [桁切替] 最初の表の DPC 階層: 6=疾患別 / 7=病態等 / 8=年齢等 / 10=手術別 / 11=処置1別 / 12=処置2別 / 13=副傷病 / 14=全14桁 (default 6)
  // [絞り込み保持 2026-07-17] サイドバー DPC 階層絞り込み (手術分類/処置1分岐/処置2分岐) の
  // 「表示階層で有効な最深コード」(7〜14 桁)。dpc14 の前方一致で絞る。
  // 下位階層の絞り込みを上位階層に反映させないゲートはクライアント側 (effectiveDpcSelection) で実施。
  selDpc?:     string | null;
}
export interface DiseaseRankingRow {
  dpc6:          string;        // 選択レベルの DPC コード
  疾患名:        string;        // 選択レベルの名称
  // [候補K 2026-07-17] 処置1/2 分岐に対応する候補Kコード・名称 (公式対応表 sheet9/10)。
  // 「実績Kごとの行分割」は不可 (fact の proc1/proc2 は分岐値 1 文字のみ収集) のため、
  // 分岐 ↔ K の対応をホバーで見せる方式。" / " 区切り。対象外レベル/該当なし分岐は ''。
  処置1候補K:    string;
  処置1候補名:   string;
  処置2候補K:    string;
  処置2候補名:   string;
  mdc2:          string;
  // [7/8桁 2026-09-28] 7桁目=病態等分類 / 8桁目=年齢・出生時体重・JCS等 の分岐名 (dim_dpc7/8)。
  //   その桁が x (= この疾患では分岐しない) または表示階層が浅い場合は ''。
  病態等:        string;
  年齢等:        string;
  // [次の分岐へ 2026-09-28] この行をクリックしたときに飛ぶ階層 (7/8/10/11/12/13/14)。
  //   DPC マスタ (dim_dpc14_tensu) + 期間内の実データのコードで、この行の配下が
  //   次に 2 つ以上に分かれる階層。どこでも分かれなければ 14 (完全)。14 桁表示の行と
  //   14 桁未確定 (コードが表示階層より短い) の行は null (= それ以上 drill しない)。
  next_level:    number | null;
  手術名:        string;        // [桁切替] 10桁以上=手術名 (dim_dpc10.opecd_name)、6桁=空
  処置1名:       string;        // [桁切替] 11桁以上=手術・処置等1 (dim_dpc11)、それ未満=空
  処置2名:       string;        // [桁切替] 12桁以上=手術・処置等2 (dim_dpc12)、それ未満=空
  副傷病:        string;        // [桁切替] 13桁以上=副傷病 (dim_dpc13)、それ未満=空
  重症度:        string;        // [桁切替] 14桁=重症度 (dim_dpc14)、それ未満=空 — ホバー用
  副傷病実績:      string;       // [実績 2026-07-01] グループ内の実績副傷病(医療資源病名2・件数降順) — 13桁目ホバー用
  // [分岐該当/併施の分離 2026-07-22] 008b 実績処置K を、公式対応表 (dim_dpc11/12_cand) に
  //   その入院自身の 11/12 桁目で当てて 2 つに振り分ける。ホバーで書き分けるため。
  処置1実績:       string;       // 11桁目の分岐に該当する実績処置1K+名称(件数降順)
  処置2実績:       string;       // 12桁目の分岐に該当する実績処置2K+名称(件数降順)
  処置1実績_併施:  string;       // 分岐外(併施)の実績処置1K — 11桁目の採番には効かない
  処置2実績_併施:  string;       // 分岐外(併施)の実績処置2K — 12桁目の採番には効かない
  icd_list:      string;        // [実績] グループ内の実績ICD病名(上位10、" / "区切り) — 疾患ホバー用
  ope_list:      string;        // [実績] グループ内の実績手術K+名称(上位10) — 手術ホバー用
  // 2026-05-26 改訂 (病院統計の正式定義に揃える):
  //   症例数 (n_cases) = 期間内の新規入院数 (= SUM(new_admission) = date == 入院初日 の日数)
  //   平均在院日数 (alos) = 在院延数 / ((新規入院 + 他科から転入 + 他科への転出 + 退院) / 2)
  //     - 大粒度/中粒度/小粒度 では fact_disease_day.dep_<粒度>_in/out を使う
  //     - 医師指定時は dr_in/dr_out を使う
  n_cases:       number;      // 症例数 (= 新規入院数)
  los_total:     number;      // 入院患者延数 (= ∑inpatient、 退院日も 1 計上、 30 系 _収入分析_集計コア.R l.67 と同定義)
  alos:          number;      // 平均在院日数 (正式定義)
  dpc2_pct:      number;      // DPC II 以内 % (∑dpc2_days / ∑los)
  // [中央値 2026-07-22 ユーザ指示] 1日単価は「患者(=急性期エピソード)1人ずつの単価」の中央値。
  //   旧定義 (∑収益/∑los の日数加重平均) は長期入院1例で分母が膨らみ単価が下へ引っ張られた。
  price_per_day: number;      // 1 日入院単価 = MEDIAN(収益_日 / los_episode)
  price_per_day_dpc2in:  number; // 1 日入院単価 — DPC II 以内日のみ (加重・表からは非表示)
  price_per_day_dpc2out: number; // 1 日入院単価 — DPC II 超日のみ (加重・表からは非表示)
  gp_per_day:    number;      // 1 日粗利単価 = MEDIAN(粗利_日 / los_episode) ※粗利_日 = 046 由来 `gp`
  gp_per_day_dpc2in:  number; // 1 日粗利単価 — DPC II 以内日のみ
  gp_per_day_dpc2out: number; // 1 日粗利単価 — DPC II 超日のみ
  revenue_total: number;      // 期間累計入院収入 (円)。 表示は monthsEffective で割って月平均化
  gp_total:      number;      // 期間累計粗利 (円) = ∑粗利_日 (= 046 由来 `gp` SUM、 30 系完全準拠)
}
// 期間内集計サマリ (表ヘッダ下に表示):
//   ranking と同じ WHERE 条件 (期間 / dep / dpcfy / dpc6 / admdrcd / yotei / iryo) で
//   表の合計を 1 query で取る。 「(未分類)」 と 「収益欠損」 は ranking と同様除外。
//   - n_cases_total:   ∑ new_admission  (新規入院 件)
//   - discharge_total: ∑ discharge      (退院 件)
//   - los_total:       ∑ inpatient      (入院患者延数 日、 退院日含む = 30 系完全一致)
//   - revenue_total:   ∑ 収益_日        (入院収入 円)
//   - gp_total:        ∑粗利_日 (= 046 由来 `gp` SUM、 30 系完全準拠)
export interface DiseaseRankingSummary {
  n_cases_total:   number;   // [改修版S1] 退院数(退院エピソード数)
  discharge_total: number;   // 退院数(= n_cases_total と同義)
  los_total:       number;   // [改修版S1] 入院延日数 Σ(退院日-入院日+1)
  los_median:      number;   // [改修版S1] 入院日数中央値 median(退院日-入院日+1)
  revenue_total:   number;
  gp_total:        number;
}
// [ICD表 2026-06-26] 医療資源病名ICD別の退院数ランキング(表6の左に表示)。
export interface DiseaseRankingIcdRow {
  icd:           string;   // 医療資源病名ICDコード(修飾語分散を束ねる軸)
  byomei:        string;   // 代表病名(mode正規化済)
  n_cases:       number;   // 退院数
  los_total:     number;   // 入院延日数
  alos:          number;   // 入院日数中央値
  revenue_total: number;
  gp_total:      number;
}
export interface DiseaseRankingIcdResponse {
  rows:  DiseaseRankingIcdRow[];
  sqlMs: number;
}
export interface DiseaseRankingResponse {
  rows:        DiseaseRankingRow[];
  sqlMs:       number;
  totalCases:  number;        // 期間内全症例数 (除外前)
  months:      number;        // 期間月数 (dateFrom〜dateTo / 30.4 round、 最低 1)。
                              // 「直近 N ヶ月」 等の期間表示用 (= ユーザが指定した期間そのもの)。
  monthsEffective: number;    // 実効月数 = 期間内で 収益データが入っている月数。
                              // 例: 直近 12 ヶ月 (months=12) だが 直近月の 2026-05 が
                              // 収益 NULL (会計未確定) なら monthsEffective=11。
                              // フロントの月平均化 (n_cases / monthsEffective) で
                              // 「未確定月のせいで月平均が薄まる」 問題を回避するため。
                              // 最低 1 (0 ヶ月期間で division by zero 回避)。
  excludedReasons?: { 未分類: number; 収益欠損: number };
  summary?:         DiseaseRankingSummary;  // 期間内集計サマリ (ranking と同じ WHERE)
}

export interface DoctorListRow {
  admdrcd: string;
  医師名:  string;
  主科:    string;
  n_cases: number;            // 直近 12 ヶ月 (asOf 起点) の症例数
}
export interface DoctorListResponse {
  rows:  DoctorListRow[];
  sqlMs: number;
}

// ── /api/query/disease_ranking_kcode (疾患タブ最下部 表 6/7/8 = 点数表区分番号) ──
// fact_kcode_per_admission (= 020_keiro 由来) を type 別に group by。
//   type='ope'   → 表 6: 手術                  (DPC 9-10 桁、 K コード等)
//   type='proc1' → 表 7: 手術・処置等 1        (DPC 11 桁)
//   type='proc2' → 表 8: 手術・処置等 2        (DPC 12 桁)
// 名称は dim_kcode_<type> (= ATTACH 'dpchier') から引く。
// 共通 filter は disease_ranking と完全同期 (期間 / dep / dpcfy / dpc6 / admdrcd /
// 予定緊急 / 医療区分)。 fact_kcode_per_admission には期間情報がないため、
// fact_disease_month と (ptid_anon, admdate_month, admit_seq) で内部 join して filter を適用する。
// 実績 0 件 (= マスタにあるが実績なし) は HAVING で除外。
export type KcodeType = "ope" | "proc1" | "proc2";

export interface DiseaseRankingKcodeFilters {
  type:        KcodeType;
  // 期間 / 共通 filter (disease_ranking と同じ)
  dateFrom:    string;
  dateTo:      string;
  dep?:        string | null;
  granularity?: Granularity;
  dpcfy?:      number | null;
  dpc6?:       string | null;
  admdrcd?:    string | null;
  yotei?:      string | null;
  iryo?:       string | null;
  // 上の表 (表 2-5 = DPC 階層 drill-down) で選択中の DPC コード階層。
  // 渡されていれば対応する桁数で f.dpc14 を絞り込む (= 「脳梗塞 010060
  // 010060xx99x40x の手術別」 のような連動)。
  selectedDpc10?: string | null;
  selectedDpc11?: string | null;
  selectedDpc12?: string | null;
  selectedDpc14?: string | null;
  // 表 6 (ope) のみ KKK0 / KKK1 (= ダミー: 手術なし / 手術あり) を除外するか
  // (default true)。
  excludeDummyOpe?: boolean;
  // [分岐該当のみ 2026-07-18] 選択中分岐の候補K (公式対応表・dim_dpc11/12_cand) に一致する
  // 実績Kだけに絞る (併施を除外)。branch11/12 = 選択中の 11/12 桁コード。
  branchOnly?: boolean;
  branch11?: string | null;
  branch12?: string | null;
  // [2026-09-29] 主表の表示階層 (7 以上なら主表と同じく dpc14 未確定の退院を対象から外す)
  level?: number | null;
}

export interface DiseaseRankingKcodeRow {
  kcode:   string;
  name:    string;       // dim から引いた名称。 マスタ未登録なら kcode と同じ。
  // この (type, kcode) を伴う入院の「代表 DPC コード」 (= 最頻 mode 値)。
  // 桁数は type に応じる:
  //   ope   → DPC 10 桁 (= 上 10 桁、 手術定義テーブル相当)
  //   proc1 → DPC 11 桁 (= 手術・処置等 1 相当)
  //   proc2 → DPC 12 桁 (= 手術・処置等 2 相当)
  // 該当入院に dpc14 が無い (= dpc6 のみ確定) ケースが多いと NULL になりうる。
  top_dpc: string | null;
  n_cases: number;       // 件数 = この (type, kcode) が実施された退院の数 (DISTINCT 入院 key・併施も含む)
  // [2026-09-29 ユーザ指示] 退院数 = この K を「その退院のメイン」として数えた退院の数。1 退院は 1 つの K にだけ入る
  //   (処置1/2 = その退院の 11/12 桁目を決めた処置 (公式対応表で照合)・手術 = KKK0/KKK1 以外の手術 K)。
  //   同じ退院に候補が複数あるときはコード順で先頭 (元データに順番が無いため)。
  //   Σ n_main + none_main = 主表の退院数 (同じ対象退院)。
  n_main: number;
}

export interface DiseaseRankingKcodeResponse {
  type:  KcodeType;
  rows:  DiseaseRankingKcodeRow[];
  sqlMs: number;
  // [2026-09-29] 対象退院の数 (= 主表の退院数と同じ集合) と、メインの K が無い退院の数
  //   (処置1/2 = 分岐を決めた処置なし・手術 = 手術なし/定義外 KKK0/KKK1 のみ)。
  total_adm: number;
  none_main: number;
}

// ── /api/query/dpc6_list (疾患深掘り — 疾患名検索 dropdown 用) ─────────
// dim_dpc6 マスタ全件 (515 件) + asOf 指定時のみ直近 12 ヶ月の出現件数 n_cases。
// FilterBar の DiseaseSelector で 「脳梗塞」 等の疾患名検索 datalist 候補に使う。
// 医師は DPC コード (6桁数字) を覚えていないため、 疾患名で検索できることが必須。
export interface Dpc6ListRow {
  dpc6:     string;            // "010060"
  疾患名:   string;            // "脳梗塞"
  mdc2:     string;            // "01"
  n_cases?: number;            // 直近 12 ヶ月の出現件数 (asOf 指定時のみ)
}
export interface Dpc6ListResponse {
  rows:  Dpc6ListRow[];
  sqlMs: number;
}

// ── /api/query/dpc_tensu (DPC 点数タブ — ふくろ君風 包括点数階段チャート) ──
// 14 桁 DPC コード → 入院期間 I/II/III の境界日 (days) + 1 日点数 (points)。
// 厚労省 電子点数表 sheet 16 (= 診断群分類点数表) からの抽出。
//
// マッチング: ワイルドカード "x" を含む 14 桁の bidirectional match。 完全一致 →
// より specific な候補 (= x が少ない) の順で 1 件取得。 マッチしなければ 404。
//
// 包括/出来高 区分: hokatsu_flag = "出来高" の場合、 days_*/points_* は null。
// (= sheet 16 で I 系が空欄 = 出来高算定対象 = 1 日あたり点数の概念がない)
//
// 包括金額 = points_X × days × coefficient × 10 円 (= 厚労省 公開計算式)。
// 期間 I → II → III の境界日で 1 日点数が段階的に下がる。
// 入院期間 III 超 (= days > days_III) は 出来高算定 (= chart は III の値を継続)。
export interface DpcTensuResponse {
  dpc14:        string;        // 入力された 14 桁 (= ワイルドカード解決前)
  dpcfy:        number;        // マスタ改定年度: 2024 (= R6) / 2026 (= R8)
  matched_code: string;        // マスタ側でマッチした 14 桁 (= ワイルドカード x 含む)
  name_shobyo:    string | null;
  name_shujutsu:  string | null;
  name_proc1:     string | null;
  name_proc2:     string | null;
  name_fukusho:   string | null;
  name_jusho:     string | null;
  full_name:      string;       // "傷病名 / 手術名 / 処置1 / ..." を組み立てたもの
  days_I:       number | null;
  days_II:      number | null;
  days_III:     number | null;
  points_I:     number | null;
  points_II:    number | null;
  points_III:   number | null;
  hokatsu_flag: "包括" | "出来高";
  valid_from:   string | null;  // "2024-06-01" or "99999999"
  valid_to:     string | null;
  sqlMs:        number;
}

// ── /api/query/disease_period_range (疾患深掘り — DPC 年度 → 期間 range) ──
// DPC 年度ラジオ (2024 年度 / 2026 年度) 選択時に、 その年度の range と
// 「range 内に確定済データがある」 期間 (直近 12 ヶ月、 range_start に clamp) を返す。
// range 内データ無し (例: 2026 年度開始前) なら hasData=false。
export interface DiseasePeriodRangeResponse {
  dpcfy:      number;          // 2024 / 2026
  rangeStart: string;          // "2024-06-01"
  rangeEnd:   string;          // "2026-05-31"
  dataFrom:   string | null;   // 計算済 dateFrom (range_start に clamp 済)
  dataTo:     string | null;   // 計算済 dateTo (収益確定最大月の月末)
  hasData:    boolean;         // range 内に収益確定データがあるか
}

// ── /api/query/dpc_labels (サイドバー DPC 階層チップの名称解決) ──────────
// selectedDpc10/11/12 のコードから dpchier の分岐名を引く。URL 復元時 (行クリックを経ない)
// でもチップに名称を出すため、コード → 名称の逆引きを server で行う。
export interface DpcLabelsResponse {
  name10: string | null;   // 手術分類名 (dim_dpc10.opecd_name)
  name11: string | null;   // 処置1分岐名 (dim_dpc11.name 整形済)
  name12: string | null;   // 処置2分岐名 (dim_dpc12.name 整形済)
}

// ── /api/query/dpc_branch_list (サイドバー DPC 分岐セレクタの候補) ──────
// [2026-07-18] DPC手術分類(10)/処置1分類(11)/処置2分類(12) の独立セレクタ用候補リスト。
// dim_dpc10/11/12 (= fact に実在する分岐のみ) のコード+名称。under= 上位コードで前方一致絞り。
export interface DpcBranchListRow {
  code: string;   // dpc10/dpc11/dpc12 (x 含む)
  name: string;   // "疾患名　分岐名" (例: "急性心筋梗塞…　経皮的冠動脈形成術等")
}
export interface DpcBranchListResponse {
  level: number;  // 10 / 11 / 12
  rows: DpcBranchListRow[];
}

// ── /api/query/dpc_coef (DPC 医療機関別係数・月次 effective-dated) ──────
// 源 = dashboard.duckdb の dim_dpc_coef (build_dashboard_db.R が emr_cache.duckdb の
// masijinyusysf ssicd='03036000' nsysno='A00000' から月次で取り込み。2026-07-17 新設)。
// DpcTensuPanel が「表示期間の終了月に有効な係数」を default に使う (請求月適用)。
export interface DpcCoefRow {
  valid_from: string;   // "2026-06-01"
  valid_to:   string;   // "2026-06-30"
  coef:       number;   // 1.6243
}
export interface DpcCoefResponse {
  rows: DpcCoefRow[];   // 旧 duckdb (dim 未導入) では空 → フロントは fallback
}

// ── /api/query/dept_metrics_all (v2.18 — 予算・前年比較 タブ用) ───────
// 単一診療科 (or 病院全体 anchor) × 全指標 × {当月 / 前月 / 前年同月 / 予算 / 期間 trend (actual + budget + prevYear)}
// 「予算・前年比較」 タブで Cards / Charts / Table を 1 リクエストで描画するために 1 ショット返す。
// 派生指標 (出来高換算薬剤材料費 = 出来高換算薬剤費+出来高換算材料費 等) は client 側で base 指標から計算。
export interface DeptMetricsAllFilters {
  granularity:  Granularity;
  iryo:         Iryo;
  yotei:        Yotei;
  asOfMonth:    string;        // YYYY-MM-DD
  periodFrom:   string;        // YYYY-MM-DD: trend の起点 (kpi endpoint と同じ意味)
  focusedDept?: string;        // undefined or "" → 病院全体 anchor
  trendBack?:   number;        // trend を asOfMonth から何ヶ月遡るか (既定 11=直近12ヶ月)。
                               // 直近12ヶ月平均モードは 23 を指定して 24ヶ月取得 (移動平均13点ぶん)。
  // [2026-08-25] 入院経路タブと同じ「経由」サイドバーフィルタ (病院/総CL/その他)。
  //   "合計"/未指定 = 絞り込みなし (既存挙動と完全に同一)。fact_long_keiyu (粒度=小のみ) を使うため
  //   granularity!=="小" のときは無視される (サーバ側で granularity=="小" を要求)。
  keiyu?:       string;
}
export interface DeptMetricsTrendPoint {
  date:     string;
  actual:   number | null;
  budget:   number | null;
  prevYear: number | null;
}
export interface DeptMetricsAllRow {
  集計区分:  string;
  current:   number | null;
  prevMonth: number | null;
  prevYear:  number | null;
  budget:    number | null;
  trend:     DeptMetricsTrendPoint[];   // periodFrom 〜 asOfMonth の月次データ
}
export interface DeptMetricsAllResponse {
  asOfMonth:     string;
  diagnosedDept: string;        // 実際に集計に使った dept 名 (focusedDept or totalDept)
  trendMonths:   string[];      // 共通 x 軸 (periodFrom 〜 asOfMonth)
  rows:          DeptMetricsAllRow[];
  sqlMs:         number;
}

// ── 科別マトリックス (入院実績(前年度・予算比較) タブ・全科 × 指標 × FY12ヶ月) ──
export interface DeptMatrixFilters {
  granularity: Granularity;
  iryo:        Iryo;
  yotei:       Yotei;
  fyStart:     string;    // "YYYY-04-01"
  fyEnd:       string;    // "YYYY+1-03-01"
  metrics:     string[];  // fact_long_all の集計区分 + 合成指標 "手術収入"/"手術件数"
  // [2026-08-25] 経由フィルタ (病院/総CL/その他)。"合計"/未指定 = 絞り込みなし。
  //   fact_long_keiyu (粒度=小のみ) を使うため granularity!=="小" のときは無視される。
  keiyu?:      string;
}
export interface DeptMatrixCell {
  診療科:   string;
  集計区分: string;
  trend:    DeptMetricsTrendPoint[];   // fyStart〜fyEnd の月次 (actual/budget/prevYear)
}
export interface DeptMatrixResponse {
  months:    string[];   // fyStart〜fyEnd
  totalDept: string;     // 病院全体行の 診療科 名 (総合計 等)
  rows:      DeptMatrixCell[];
  sqlMs:     number;
}

// ── 医師別マトリックス (入院実績(医師別) タブ・2026-08-03) ──────────────
// 医師 × 在院診療科 × 指標 × FY12ヶ月。源 = fact_disease_month (= fact_long_all と同一の
// disease_day 由来。病院全体・科別の合計は fact_long_all と 0 差で一致することを実測済)。
//   - 予算は医師別に存在しない (fact_budget_all は科別まで) → trend の budget は常に null。
//   - 手術収入 / 手術件数 / 紹介入院率 / 常勤換算数 は医師軸のデータが無いので列に出さない。
//   - 診療科は「その在院日の診療科」なので 1 医師が複数科に跨る (実測 14/106 人)。
//     行は 医師 × 診療科 で返し、科フォーカス時はその科ぶんだけを見る (= 科別 1 行と整合)。
export interface DoctorMatrixFilters {
  admdrcd?: string;
  granularity: Granularity;
  iryo:        Iryo;
  yotei:       Yotei;
  fyStart:     string;    // "YYYY-04-01"
  fyEnd:       string;    // "YYYY+1-03-01"
}
/** 医師別の月次点。予算が無いので budget を持たない (= 科別より 1 フィールド軽い)。 */
export interface DoctorMatrixPoint {
  date:     string;
  actual:   number | null;
  prevYear: number | null;
}
export interface DoctorMatrixCell {
  admdrcd:  string;
  医師名:   string;
  診療科:   string;
  集計区分: string;
  /** ★疎配列: 今年度も前年度も値が無い月は入れない (医師 × 科 × 指標 × 12ヶ月を
   *  全部埋めると 2.3MB になるため。読み手は date で引くので欠落月 = 実績ゼロ)。 */
  trend:    DoctorMatrixPoint[];
}
export interface DoctorMatrixResponse {
  months:  string[];   // fyStart〜fyEnd
  rows:    DoctorMatrixCell[];
  sqlMs:   number;
}

// ── 条件セット (presets): 分析タブの設定保存 ────────────────────────────
export interface CorrelationPresetConfig {
  sel?: string[];           // 選択中の指標 key (順序保持・先頭=基準)。 新形式。
  y?:   string;             // 旧形式 (後方互換): y軸 spec.key
  x?:   string[];           // 旧形式 (後方互換): x軸 spec.key
  from: string;             // corrFrom "YYYY-MM-01"
  to:   string;             // corrTo  "YYYY-MM-01" ("" = 最新月)
  gran: Granularity;
  dept: string | null;      // focusedDept (null = 病院全体)
}
export interface Preset {
  id:         number;
  feature:    string;
  name:       string;
  ownerEmail: string;
  isShared:   boolean;      // 全ユーザー共有 (管理者作成)
  mine:       boolean;      // 自分が作成
  canDelete:  boolean;
  config:     CorrelationPresetConfig | null;
  createdAt:  string;
}
export interface PresetsResponse { presets: Preset[]; }

// ── /api/query/meisai_movers (2026-06-16 — 明細分析: 診療行為/加算の増減 ranking) ──
// 別ファイル meisai_act.duckdb (ym×医事コード×名称×診療No×入外×点数/金額) を直読み。
export interface MeisaiMoversFilters {
  asOfMonth: string;                    // YYYY-MM-DD: 期間の終点
  io?:       "入院" | "外来" | "全";    // 既定 入院
  sinryoNo?: string;                    // 診療No分類で絞り込み (未指定=全)
  dept?:     string;                    // 診療科で絞り込み (未指定=全)
  basis?:    "ten" | "yen";             // 点数 or 金額 (既定 ten=点数)
  window?:   number;                    // 比較窓 月数 (1=選択月vs前年同月 / 12=直近12ヶ月vs前年同期。 既定 12)
  topN?:     number;                    // 上位件数 (既定 30, 最大 100)
}
export interface MeisaiMoverRow {
  code:     string;
  name:     string;
  sinryoNo: string;
  cur:      number | null;   // 当期 合計 (点 or 円)
  prev:     number | null;   // 前年同期 合計
  diff:     number | null;   // cur - prev
  ratio:    number | null;   // cur / prev
  curCnt:   number | null;   // 当期 件数 (回数)
  prevCnt:  number | null;   // 前年同期 件数 (回数)
}
export interface MeisaiMoversResponse {
  available:   boolean;            // meisai_act.duckdb が無ければ false (タブは未生成表示)
  sinryoNos:   { no: string; name: string }[];  // フィルタ用 診療No分類 (番号順、 表示は "11:初診")
  depts:       string[];           // フィルタ用 診療科 一覧 (volume 降順)
  increasers:  MeisaiMoverRow[];   // 増加TOP (diff 降順)
  decreasers:  MeisaiMoverRow[];   // 減少TOP (diff 昇順)
  sqlMs:       number;
}

// ── 粗利分析タブ ──
export interface ProfitRow {
  key:  string;
  name: string;
  // [階層列 2026-07-17] DPC別 (退院日基準・dpc 軸) のみ値が入る。DPC分析主表と同じ
  // 手術分類/処置1分岐/処置2分岐 の名称 (手術別=10桁以上/処置1別=11桁以上/処置2別=12桁以上)。
  手術名?:  string;
  処置1名?: string;
  処置2名?: string;
  // [ホバー 2026-07-18] dpc 軸のみ: DPC分析主表と同じ実績リスト (件数降順・" / "区切り) + 候補K
  icd_list?:    string;
  ope_list?:    string;
  処置1実績?:   string;      // [2026-07-22] 11桁目の分岐に該当する実績処置K のみ
  処置2実績?:   string;      // [2026-07-22] 12桁目の分岐に該当する実績処置K のみ
  処置1実績_併施?: string;   // 分岐外(併施)の実績処置K
  処置2実績_併施?: string;
  処置1候補K?:  string;
  処置1候補名?: string;
  処置2候補K?:  string;
  処置2候補名?: string;
  // [2026-07-18] 退院日基準のみ: 入院日数中央値 (急性期在院日数の中央値・在院日基準は null)
  alos?: number | null;
  収入: number;                  // 月平均 (円)
  // ★2026-08-22: null をとる (期間内に GP を算出できない月がある = gpComplete=false のとき)
  粗利: number | null;           // 月平均 (円)
  延数: number;                  // 月平均 (人日)
  // [中央値 2026-07-22] DPC別 (dpc 軸・退院日基準) は「患者1人ずつの1日単価」の中央値、
  //   科別/医師別/在院日基準は従来どおり加重平均 (Σ/Σ)。どちらかは unitStat で判別する。
  "1日入院単価": number | null;  // 中央値 or 加重 Σ収入/Σ延数 (円/日)
  "1日粗利単価": number | null;  // 中央値 or 加重 Σ粗利/Σ延数 (円/日)
  粗利率: number | null;          // 加重 Σ粗利/Σ収入 (0-1)
  // [医師人件費比 2026-08-24 ユーザ指示] GP分析の GP 列の手前に出す 2 列。
  //   医師人件費   = 月平均 (円)。診療科へ按分済 (常勤換算>0 の医師のみ・直近12ヶ月平均で賞与を均す)
  //   医師人件費比 = Σ粗利 / Σ医師人件費 (倍)。率ではなく**倍率**なので画面は小数第2位まで。
  //   ★分子・分母それぞれの被覆を独立に見る (gpComplete / jinComplete)。どちらか欠ければ null。
  // 薬剤材料費 = 出来高換算薬剤費 + 出来高換算材料費 (月平均・円)。★両モードで同じ値。
  薬剤材料費: number | null;
  医師人件費: number | null;
  医師人件費比: number | null;
  件数: number;                  // 期間内エピソード数
}
export interface ProfitResponse {
  rows:    ProfitRow[];
  nMonths: number;
  volBase: string;
  basis?:  "在院" | "退院";
  // [中央値 2026-07-22] 1日単価列の統計量。"median"=患者1人ずつの単価の中央値 (DPC別のみ)、
  //   "weighted"=Σ分子/Σ分母 の加重平均 (科別/医師別/在院日基準)。UI の ※注記に使う。
  unitStat?: "median" | "weighted";
  // [GP の被覆 2026-08-22] 期間内で GP を算出できた月数。nMonths 未満のとき gpComplete=false
  //   となり、粗利/1日粗利単価/粗利率 は null で返る (「GP がある月だけの合計を期間の全月数で
  //   割った偽の平均」を出さないため)。θ版 (GP=薬剤材料費差引後−医師人件費) は医師人件費が
  //   揃う 2025-04 以降しか GP を持たないので、それ以前を含む期間で false になる。
  gpMonths?: number;
  gpComplete?: boolean;
  // [医師人件費比 2026-08-24] 分母 (医師人件費) の被覆。正式版は GP=薬剤材料費差引後 なので
  //   gpComplete が true でも医師人件費が無い期間がある → **独立に**数えないと
  //   「分子は全月・分母は一部の月」の偽の比になる。
  jinMonths?: number;
  jinComplete?: boolean;
}
// ── 行為別売上分析(ベータ)──────────────────────────────────────────
// [2026-07-18 改修] 医師軸廃止・医療区分/予定緊急 filter・table は 診療科×月×行為区分。
export interface KbActFilters {
  granularity?: string;
  dateFrom:    string;
  dateTo:      string;
  iryo?:       string | null;   // "急性期"/"回復期"・null=合計
  yotei?:      string | null;   // "予定"/"緊急"・null=合計
  basis?:      string | null;   // [2026-07-18] "粗利"=材料・薬剤(RP明細由来)を控除。null/他=入院収入
}
export interface KbActSeriesRow { month: string; 行為区分: string; 売上: number; }
export interface KbActTableRow  { key: string; month: string; 行為区分: string; 売上: number; }
export interface KbActResponse {
  series:  KbActSeriesRow[];   // 月 × 行為区分(病院全体・チャート用)
  table:   KbActTableRow[];    // 診療科 × 月 × 行為区分(科×年月マトリクス用)
  nMonths: number;
}

// ── 入院料分析 (β) ────────────────────────────────────────────────
export interface InpatientFeeAnalysisFilters { dateFrom: string; dateTo: string; }
export interface InpatientFeeMonthlyRow {
  month: string; category: string; cases: number; patientDays: number; revenue: number;
}
export interface InpatientFeeInclusionRow {
  month: string; category: string; inclusion: string; inclusionCode: string;
  patientDays: number; revenue: number;
}
export interface InpatientFeeOccupancyRow {
  month: string; unit: string; bedDays: number; midnightPatientDays: number;
  directDischargePatientDays: number; denominatorPatientDays: number;
  matchedMidnightPatientDays: number; matchedPatientDays: number;
  reviewPatientDays: number; outsideBilledDays: number; matchedRevenue: number;
  occupancyRate: number | null; expectedFeeBedRate: number | null;
  occupiedExpectedMixRate: number | null; matchRate: number | null;
}
export interface InpatientFeeOccupancyDailyRow {
  date: string; unit: string; bedDays: number; midnightPatientDays: number;
  directDischargePatientDays: number; denominatorPatientDays: number;
  matchedMidnightPatientDays: number; matchedPatientDays: number;
  reviewPatientDays: number; outsideBilledDays: number;
  occupancyRate: number | null; expectedFeeBedRate: number | null;
  occupiedExpectedMixRate: number | null; matchRate: number | null;
}
export interface InpatientFeeRoomDetailRow {
  month: string; room: string; expectedCategory: string; billedCategory: string;
  status: string; patientDays: number; revenue: number; inpatientRevenue: number;
}
export interface ShortStay3Row {
  month: string; accountingCode: string; itemName: string;
  cases: number; patientDays: number; revenue: number;
}
export interface InpatientFeeRecoverySpecialRow {
  month: string; category: string; ward: string; dept: string; kouseicd: string;
  cases: number; patientDays: number; revenue: number;
}
export interface InpatientFeeRevisionRow {
  order: number; category: string; payment: string; dayBand: string; accountingCode: string;
  beforeKouseicd: string | null; afterKouseicd: string; itemName: string;
  beforePoints: number | null; afterPoints: number; pointDelta: number | null;
  changeRate: number | null; status: string;
}
export interface InpatientFeeAnalysisResponse {
  monthly: InpatientFeeMonthlyRow[];
  inclusion: InpatientFeeInclusionRow[];
  occupancy: InpatientFeeOccupancyRow[];
  occupancyDaily: InpatientFeeOccupancyDailyRow[];
  roomDetail: InpatientFeeRoomDetailRow[];
  recoverySpecial: InpatientFeeRecoverySpecialRow[];
  revision: InpatientFeeRevisionRow[];
  shortStay: ShortStay3Row[];
}
export interface ShortStay3AnalysisFilters { dateFrom: string; dateTo: string; }
// 室料差額(開発版): 差額室の在室日×徴収区分。金額はすべて税込。
export type RoomChargeCategory = "満額" | "減額" | "旧料金継続" | "計算対象外" | "未徴収";
export interface RoomChargeMonthlyRow {
  month: string; source: "確定" | "速報"; days: number; calendarDays: number;
  category: RoomChargeCategory; price: number; newRoom: boolean; patientDays: number; amount: number;
  /** 基準料金(室料マスタの区分01＝本来の料金)がある在室日と、その基準料金×日数・基準より安く取った差額 */
  baseDays: number; baseAmount: number; reducedYen: number;
}
export interface RoomChargeRoomRow {
  room: string; /** 表示名（4418 → 4D-18） */ label: string; oldPrice: number | null;
  newPrice: number | null; /** 新料金表の税別の額 */ newPriceExTax: number | null; newRoom: boolean;
}
export interface RoomChargeMeta {
  revisionMonth: string; confirmedTo: string; preliminaryTo: string | null; rooms: number; newRooms: number;
}
export interface RoomChargeTransitionRow {
  month: string; oldPrice: number | null; newPrice: number | null;
  category: RoomChargeCategory; patientDays: number; amount: number;
}
export interface RoomChargeResponse {
  transitions?: RoomChargeTransitionRow[];
  monthly: RoomChargeMonthlyRow[]; rooms: RoomChargeRoomRow[]; meta: RoomChargeMeta | null;
}
// 急性期総合体制加算3(開発版): 手術等の集積性の要件を年度累計ペースで見張る。件数は日別(患者IDなし)。
export type Kyusou3Source = "確定" | "速報";
export interface Kyusou3DailyRow { date: string; fy: number; code: string; count: number; source: Kyusou3Source; }
export interface Kyusou3Requirement {
  code: string; symbol: string; name: string; kind: "必須" | "選択"; threshold: number; unit: string;
  notice: string; order: number; note: string | null;
}
export interface Kyusou3Meta {
  fy: number; prevFy: number; asOf: string; confirmedTo: string; prelimFrom: string | null; prelimTo: string | null;
  fetchedAt: string; server: string; master: string;
  /** フィードを回した指定日(既定=実行日の前日)。データ時点との差 = 速報の入力待ち */
  requestedTo: string; qualityError: string | null;
}
export interface Kyusou3QualityRow { month: string; code: string; confirmed: number; prelim: number; diff: number; }
export interface Kyusou3PaceResponse {
  daily: Kyusou3DailyRow[]; requirements: Kyusou3Requirement[]; meta: Kyusou3Meta | null; quality: Kyusou3QualityRow[];
}
export interface ShortStay3EpisodeHistogramRow {
  month: string; kcode: string | null; accountingCode: string; itemName: string; dept: string;
  completed: boolean; lengthOfStay: number | null; preOpDays: number | null; postOpDischargeDays: number | null;
  admissionWeekday: number; dischargeWeekday: number;
  cases: number; inpatientFeeRevenue: number; inpatientRevenue: number;
}
export interface ShortStay3DefinitionRow {
  accountingCode: string; itemName: string; points: number;
  effectiveFrom: string; effectiveTo: string; bundleDays: number;
}
export interface ShortStay3AnalysisResponse {
  histogram: ShortStay3EpisodeHistogramRow[];
  definitions: ShortStay3DefinitionRow[];
}

// ── 手術売上 (主たる手術別・ベータ 2026-07-18) ─────────────────────────
//   fact_surgery_rp: 手術RP単位 (手術/手術材料/手術薬剤。輸血・麻酔除外・診療科=入院主科=手術日の在院科)。
//   点数分布 = 中央値 + 四分位 [q1–q3]。マスタ点数 = masijitenmf R6(2024)/R8(2026) 窓。
export interface SurgeryRpFilters {
  dateFrom: string;             // YYYY-MM-DD (月初)
  dateTo:   string;
  dept?:    string | null;      // 入院主科 (granularity の粒度)。null=全科
  granularity?: string;         // 小/中/大
  yotei?:   string | null;      // "予定"/"緊急"・null=合計
  procedureQuery?: string | null; // Kコード / 術式名の部分一致。null/空=全術式
  monthlyFrom?: string | null;  // YYYY-MM-DD (月初)。月次推移グラフの開始月。null=dateTo の11ヶ月前
}
export interface SurgeryRpRow {
  tencd: string;                // 同じ K コード配下のレセ電コード (カンマ区切り・監査用)
  kcode: string;                // 区分番号 (K552等・masijitenmf.kouseicd。2026窓に無ければ2024窓→全期間で fallback)
  kname: string;                // 配下で実績件数が最も多い名称（表示代表）
  tencd_count: number;          // 同じ K コードに束ねたレセ電コード数
  n: number;                    // n = 会計件数 (手術RP数。手術台帳の件数とは別物)
  ten2024: number | null; ten2026: number | null;   // マスタ点数 (点・一般)
  gi_med: number; gi_q1: number; gi_q3: number;       // 手技円/件 (=粗利)
  zai_med: number; zai_q1: number; zai_q3: number;    // 材料円/件
  gi_sum: number;                                     // 手技円/合計
  zai_sum: number; yaku_sum: number;                  // 材料円/合計・薬剤円/合計
  total_sum: number;                                  // 総額/合計 (手技+材料+薬剤・円)
}
export interface SurgeryRpSummaryRow {
  dept: string; n: number;      // 手術会計件数
  total_en: number;             // 手術総金額 (円)
  gi_en: number; zai_en: number; yaku_en: number;   // 手技/材料/薬剤金額 (円)
}
// 月次推移グラフ用 (2026-08-21): 選択期間とは独立に、直近12ヶ月 (選択期間が長ければその全体) を
//   ym × 診療科 で返す。科フォーカス/複数科比較はクライアントで合算する。
export interface SurgeryRpMonthlyRow {
  ym: string;                   // "YYYYMM"
  dept: string;                 // 入院主科 (granularity の粒度・summary.dept と同キー)
  n: number;                    // 手術会計件数
  total_en: number;             // 手術総金額 (円)
  gi_en: number; zai_en: number; yaku_en: number;   // 手技/材料/薬剤金額 (円)
}
// 手術別 件数の月次推移 (2026-09-18 ユーザ指示): ②TOP20 の直下に置く件数マトリクス表の源。
//   ym × K コードの件数。診療科/予定緊急/術式検索は適用済み、
//   期間は monthly と同じ窓 (直近12ヶ月。選択期間が12ヶ月より長ければ期間全体)。
//   ★母集団が売上系と1点違う = **金額0の「まるめ」RP (短期滞在手術等基本料3 等) を含む**
//     (2026-09-18 ユーザ裁定。件数として実勢を出すため)。よって rows[].n とは一致しない。
//   行を TOP20 に絞るのはクライアント側。
export interface SurgeryRpProcedureMonthlyRow {
  ym: string;                   // "YYYYMM"
  kcode: string;                // rows[].kcode と同キー
  n: number;                    // 手術件数 (まるめRP込み)
  // 金額 (円)。術式を選んだときに月次推移グラフをその術式へ追従させるために使う。
  total_en: number; gi_en: number; zai_en: number; yaku_en: number;
}
export interface SurgeryProcedureOption {
  kcode: string;
  kname: string;
  tencd_count: number;
}
export interface SurgeryRpResponse {
  rows: SurgeryRpRow[];
  summary: SurgeryRpSummaryRow[];
  monthly: SurgeryRpMonthlyRow[];       // 月次推移 (ym × 科・選択期間の外側も含む)
  monthlyProcedure: SurgeryRpProcedureMonthlyRow[];  // 手術別 件数の月次推移 (ym × Kコード・monthly と同期間)
  monthsWithData: string[];             // 手術RPが1行でもある ym。「0件」と「未取込の月」を分けるのに使う
  procedures: SurgeryProcedureOption[]; // 現在の期間・科条件に存在する K コード単位の検索候補（検索語自体は未適用）
  totalRp: number;
}

// ── 手術会計明細ドリル(ベータ)── 手術を会計区分×台帳分/外で分解
export interface SurgeryKaikeiRow {
  会計区分: string; 台帳: string; 入外: string;
  診療科cd: string; tencd: string; 名称: string;
  金額: number; 点数: number; 件数: number;
}
export interface SurgeryKaikeiResponse { rows: SurgeryKaikeiRow[]; }

// ── 請求会計速報 = 速報進捗ビュー(入院ペース)(ベータ)── 会計リバエン速報化 B案 ────
//   fact_billing_recon_daily(診療科cd×date×入外×bunrui の金額千円・確報 or 速報)を源に、
//   「○日=最新月の最新date」時点で 予算達成ペース / 前年比ペースを 病院全体+各科 で出す。
//   目的=経年でなく「現時点で予算達成ペースか / 前年を上回るペースか」(稲吉指示 2026-07-04)。
//   金額=千円。対象=入院メイン(病院全体+各科)+ 外来(病院全体のみ)。
export interface BillingReconRow {
  lvl:       "病院全体" | "科";
  入外:      string;               // "入院" | "外来"
  cd:        string | null;        // 診療科cd(4桁・病院全体行は null)
  名称:      string;               // 診療科名 / "病院全体"
  実績:      number | null;        // 当月 1日〜○日 累計(千円)
  予算:      number | null;        // 月予算 × ○ ÷ 月日数(按分・千円)
  前年実績:  number | null;        // 前年同月 1日〜同○日 累計(確報・千円)
}
// ○日メタ(ヘッダ表示: 「○日時点(YYYY年M月D日)・速報/確報」)
export interface BillingReconAsOf {
  date:        string;   // "YYYY-MM-DD"(○日 = 最新月の最新date)
  month:       string;   // "YYYY-MM-01"(最新月)
  day:         number;   // ○(cutoff の日)
  daysInMonth: number;   // その月の日数
  prevMonth:   string;   // "YYYY-MM-01"(前年同月)
  basis:       string;   // "確報" | "速報"(最新月の basis)
}
// [汎用フォーマット 2026-07-04] モード切替(入院/外来/病院全体)+ 入院ドリルダウン用に
//   endpoint を「科×診療No(bunrui)粒度の明細 detail」+「表示科別予算 deptBudget」+「病院全体 hospital」で拡張。
//   フロントは detail を任意軸(入外/表示科/bunrui)で集約してドリル 3 表を描画する。
//   ★rows(旧: 病院全体+各科の集計行)は後方互換のため残す(既存タブが壊れないよう endpoint が両方返す)。

// 科×診療No粒度の明細 1 行(実績=当月1日〜○日累計 / 前年実績=前年同月同○日累計・確報。dim_dept_display fold 後)。
export interface BillingReconDetailRow {
  入外:     string;          // "入院" | "外来"
  表示科cd: string;          // dim_dept_display で fold 後の表示科cd(4桁)
  表示科名: string;          // 表示科名(dim_dept_display.表示科名)
  bunrui:   string;          // 診療識別(診療No。"10"=初再診 / "50"=手術・麻酔 …)
  実績:     number | null;   // 当月 1日〜○日 累計(千円)
  前年実績: number | null;   // 前年同月 1日〜同○日 累計(確報・千円)
}
// 表示科別の按分予算(入院のみ。外来 dept 予算は無い)。
export interface BillingReconDeptBudget {
  入外:     string;          // "入院"(現状 入院のみ・将来 外来対応の余地)
  表示科cd: string;
  予算:     number | null;   // 月予算 × ○ ÷ 月日数(按分・千円)
}
// 病院全体(入外別)の 実績 / 予算(按分) / 前年実績。
export interface BillingReconHospitalRow {
  入外:     string;          // "入院" | "外来"
  実績:     number | null;   // 当月 1日〜○日 累計(千円)
  予算:     number | null;   // 月予算 × ○ ÷ 月日数(按分・千円)
  前年実績: number | null;   // 前年同月 1日〜同○日 累計(確報・千円)
}
export interface BillingReconResponse {
  rows:       BillingReconRow[];               // (後方互換) 病院全体+各科(入院)の集計行
  detail:     BillingReconDetailRow[];         // 科×診療No 粒度の明細(モード別ドリルの源)
  deptBudget: BillingReconDeptBudget[];        // 表示科別 按分予算(入院)
  hospital:   BillingReconHospitalRow[];       // 病院全体(入外別)
  asOf:       BillingReconAsOf | null;
  builtAt:    string | null;
}

// ── 4層比較(無印/速報/確報/PL予算・2026-07-09)──────────────────────────
//   無印=生データ(datijirphd リアルタイム・前々日まで)/速報=n-family(未締め最新月)/
//   確報=d-family(締め済み)。金額千円=込み(保険×10+食事+自費)。
//   累計・日割り予算按分・達成率はフロントで計算(basis ごとにデータ最終日が異なるため)。
export interface BillingCompareDaily {
  month: string;   // 'YYYY-MM-01'
  basis: string;   // '無印' | '速報' | '確報'
  入外:  string;   // '入院' | '外来'
  date:  string;   // 'YYYY-MM-DD'
  day:   number;   // 日(1..31)
  v:     number;   // その日の金額千円(込み)
}
export interface BillingCompareBudget {
  month: string; 入外: string; 予算千円: number; daysInMonth: number;
}
export interface BillingCompareResponse {
  months:  string[];
  daily:   BillingCompareDaily[];
  budget:  BillingCompareBudget[];
  builtAt: string | null;
}

// 診療識別(bunrui = 診療No)→ 日本語名。endpoint / frontend 共有の定数(shared に置いて両側で使う)。
//   10=初診/再診 20=投薬 21=在宅 30=注射 40=処置 50=手術・麻酔 60=検査
//   70=画像 80=その他 85=労災等 90=入院基本料 92=特定入院料 97=食事 99=自費。
//   マスタ未定義の code は code 自身をラベルにフォールバック(BILLING_BUNRUI_NAME 参照側で)。
export const BILLING_BUNRUI_NAME: Record<string, string> = {
  "10": "初・再診",
  "20": "投薬",
  "21": "在宅",
  "30": "注射",
  "40": "処置",
  "50": "手術・麻酔",
  "60": "検査",
  "70": "画像診断",
  "80": "その他",
  "85": "労災等",
  "90": "入院基本料",
  "92": "特定入院料",
  "97": "食事",
  "99": "自費",
};
export function billingBunruiName(no: string): string {
  return BILLING_BUNRUI_NAME[no] ?? no;
}

export interface ProfitFilters {
  axis:          "dept" | "doctor" | "dpc";
  basis?:        "在院" | "退院";   // 期間基準: 在院日(fact_long_all・既定) / 退院日(episode)
  granularity?:  string;
  dpcLevel?:     number;
  yotei?:        string;
  iryo?:         string;            // [2026-07-17] 在院日基準のみ有効 (合計/急性期/回復期・未指定=急性期)。退院日はDPCエピソード=急性期固定

  volBase?:      string;
  dateFrom:      string;
  dateTo:        string;
  focusedDept?:  string | null;
  focusedDoctor?: string | null;
  dpcParent?:    string | null;
  // [2026-08-25] 経由フィルタ (病院/総CL/その他)。"合計"/未指定 = 絞り込みなし。
  //   在院基準: fact_long_keiyu (医師人件費は経由非対応・部門合計を使用)。
  //   退院基準: fact_disease_episode.経由グループ (dept/doctor/dpc の3軸すべてに反映)。
  keiyu?:        string | null;
}

// ---- 速報売上(beta・2026-07-13) -----------------------------------------
// 源 = 明細再現チェーン(会計RP請求金額付与PJ)の dash/sokuhou_*.parquet →
//      build_sokuhou_uriage.R が fact_sokuhou_daily/kcode/rp + dim_sokuhou_meta にサイドロード。
export interface SokuhouDailyRow {
  ym: string; 診療日: string; 入外区分: string;
  手技円: number; 薬剤円: number; 材料円: number; DPC包括円: number;
}
export interface SokuhouKcodeRow {
  ym: string; KCODE: string; 入外区分: string;
  件数: number; 出来高円: number; 請求円: number; 倍率寄与円: number; 名称: string;
}
export interface SokuhouMetaRow { ym: string; generated_at: string; basis: string; note: string; }
export interface SokuhouUriageResponse {
  meta: SokuhouMetaRow[]; daily: SokuhouDailyRow[]; kcode: SokuhouKcodeRow[];
}
export interface SokuhouRpFilters { ym: string; kcode?: string; }
export interface SokuhouRpRow {
  Rpid: string; KCODE: string; 名称: string; 診療科: string; 入外区分: string; 診療日: string;
  出来高円: number; 請求円: number; 手技円: number; 薬剤円: number; 材料円: number; 倍率寄与円: number;
}
export interface SokuhouRpResponse { rows: SokuhouRpRow[]; }

// ---- 看護必要度速報(beta・2026-07-30) -----------------------------------
// 源 = ssiscope/nursing_acuity/dashboard/build_daily_feed.R。
// 個票は配信せず、D-2 上限の月初来集計だけを dashboard.duckdb にサイドロードする。
export interface NursingAcuityMetaRow {
  generated_at: string;
  run_date: string;
  cutoff_date: string;
  month_start: string;
  delay_days: number;
  source_b_max_date: string;
  source_ac_max_date: string;
  source_unit_max_date: string;
  general_beds: number;
  ambulance_annual: number;
  coefficient_pct: number;
  window_type: string;
  formal_window: string;
  pipeline_status: string;
  note: string;
}

export interface NursingAcuityRequirementRow {
  requirement_id: string;
  category: string;
  criterion: string;
  threshold_pct: number | null;
  coefficient_pct: number;
  sort_order: number;
  official_window: string;
  note: string;
  raw_pct: number | null;
  value_pct: number | null;
  denominator: number;
  numerator: number | null;
  margin_pt: number | null;
  status: string;
  monitoring: string;
  as_of_date: string;
  window_label: string;
}

export interface NursingAcuityDailyRow {
  date: string;
  requirement_id: string;
  raw_pct: number | null;
  value_pct: number | null;
  threshold_pct: number | null;
  denominator: number;
  numerator: number | null;
  margin_pt: number | null;
  status: string;
}

export interface NursingAcuityWardRow {
  ward_cd: string;
  ward: string;
  beds: number;
  denominator: number;
  numerator1: number;
  numerator2: number;
  criterion1_pct: number | null;
  criterion2_pct: number | null;
  diff1_pt: number | null;
  diff2_pt: number | null;
  signal: string;
  note: string;
}

export interface NursingAcuityItemRow {
  category: string;
  ticket: string;
  major: string;
  score_unit: string;
  item_name: string;
  points: number;
  high_intensity: number;
  numerator: number;
  denominator: number;
  share_pct: number | null;
}

export interface NursingAcuityQualityRow {
  check_id: string;
  label: string;
  value: string;
  status: string;
  detail: string;
  sort_order: number;
}

export interface NursingAcuityResponse {
  available: boolean;
  meta: NursingAcuityMetaRow | null;
  requirements: NursingAcuityRequirementRow[];
  daily: NursingAcuityDailyRow[];
  wards: NursingAcuityWardRow[];
  items: NursingAcuityItemRow[];
  quality: NursingAcuityQualityRow[];
  sqlMs: number;
}
